import { BPS_ONE } from '../damage/exact';

/**
 * **A lei da CADÊNCIA**, para os modos que contam o intervalo entre salvas.
 *
 * ## Porque ela vive aqui e não no duelo
 *
 * Era `pvpFireIntervalMilliTicks`, e a única coisa que a prendia ao duelo era o
 * sítio onde estava escrita: ela lê um intervalo-base e uma percentagem de taxa
 * e devolve um intervalo. A sala do mundo aberto conta salvas exatamente da
 * mesma maneira, e reescrever a recíproca lá seria uma segunda expressão da
 * mesma lei - a primeira a divergir seria aquela em que ninguém tocou, que é a
 * razão pela qual `composeMoveSpeedBps` e `composeMaxHealthMilli` já estão nesta
 * folha.
 *
 * `pvpFireIntervalMilliTicks` continua a existir e a chamar isto, portanto todo
 * o duelo dispara exatamente nos tiques em que disparava.
 */

/** 1000 milli-tiques é um tique. A unidade do acumulador, nomeada uma vez. */
export const MILLI_TICKS_PER_TICK = 1000;

/**
 * **O menor intervalo que esta folha admite entre duas salvas.**
 *
 * Já era o piso das duas recíprocas abaixo, escrito como um `1` literal em cada
 * uma; passa a ter um nome porque um agendador precisa de o LER para provar que
 * termina. Nomear não muda número nenhum.
 *
 * Não é um tecto de cadência: é a recusa a exprimir um intervalo de zero, que
 * seria uma salva por instante sem limite. Nenhuma magnitude de item lá chega.
 */
export const MIN_FIRE_INTERVAL_MILLI_TICKS = 1;

/**
 * **Quantas salvas um casco pode, no máximo aritmético, dever num tique.**
 *
 * Derivado, e não escolhido: um tique credita `MILLI_TICKS_PER_TICK` de tempo,
 * cada salva custa pelo menos `MIN_FIRE_INTERVAL_MILLI_TICKS`, e o acumulador
 * pode chegar ao crédito exatamente vencido — o que dá uma salva a mais.
 *
 *     1000 / 1 + 1 = 1001
 *
 * Serve para **provar que um ciclo de salvas termina**, e para nada mais. Não
 * reduz cadência nenhuma: uma cadência legal produz muito menos do que isto, e
 * um valor que o ultrapasse não é um casco — é um estado impossível, que se
 * recusa em vez de se sanear.
 */
export const MAX_VOLLEYS_PER_TICK = MILLI_TICKS_PER_TICK / MIN_FIRE_INTERVAL_MILLI_TICKS + 1;

/**
 * **O teto de salvas por segundo do CAMPO: mundo aberto e caça** (gate V28I.CF.3,
 * baixado de 20 para 16 no gate V28I.CH.11A — ADR 0138 — e de 16 para **10** no
 * gate V28I.CH.12, que é o contrato final de lançamento).
 *
 * **Dez** salvas por segundo, por nave. Decisão do dono, tomada a olhar para o
 * jogo na bancada de cadência. Com o canhão mais largo — cinco bocas Anomaly a
 * três projéteis — são `15 x 10 = 150` projéteis por segundo por nave.
 *
 * As razões dos valores anteriores não se apagam: os 20 da ADR 0133 e os 16 da
 * ADR 0138 continuam escritos lá, e este é o terceiro e último número.
 *
 * ## É um teto de EMISSÃO, e não do atributo
 *
 * O que a conta pede continua a existir inteiro: o perfil congelado guarda a
 * cadência real, a interface mostra as duas e diz por palavras qual está a
 * limitar. Nada divide, arredonda ou esconde o que um item concedeu — o
 * `CLAUDE.md` proíbe-o, e isto não o faz: o que a regra diz é quantas vezes o
 * mundo admite a emissão de uma salva, exactamente como o `OPEN_WORLD_MAX_*`
 * já fazia desde a ADR 0093.
 *
 * ## Só estes dois modos
 *
 * O **duelo não é tocado**: ele tem o seu próprio contrato e o dono disse para
 * não o aplicar lá. É por isso que a constante vive aqui, no sítio neutro que
 * os dois modos do campo atravessam, e não em `pvp/`.
 *
 * ## Uma salva é indivisível
 *
 * O teto decide QUANTAS salvas nascem, nunca quantos corpos tem uma salva: as
 * quinze entidades nascem todas ou não nasce nenhuma.
 */
export const FIELD_MAX_VOLLEYS_PER_SECOND = 10;

/**
 * **A cadência que o campo deixa emitir**, dada a que a conta pede.
 *
 * `min(pedida, teto)`, e nada mais. Uma nave mais lenta que o teto sai daqui
 * exactamente como entrou — nenhuma nave é acelerada por um teto.
 */
export function fieldEmittedVolleysPerSecond(requestedPerSecond: number): number {
  if (!Number.isFinite(requestedPerSecond) || requestedPerSecond <= 0) {
    return 0;
  }
  return Math.min(requestedPerSecond, FIELD_MAX_VOLLEYS_PER_SECOND);
}

/** Se o teto do campo está a morder esta cadência. Para a interface dizê-lo. */
export function fieldCadenceIsLimited(requestedPerSecond: number): boolean {
  return Number.isFinite(requestedPerSecond) && requestedPerSecond > FIELD_MAX_VOLLEYS_PER_SECOND;
}

/**
 * O intervalo entre salvas, em milli-tiques, com a percentagem de taxa aplicada.
 *
 * ## A fórmula, e porque não é `base x (1 - bps)`
 *
 * `base x 10000 / (10000 + bps)`.
 *
 * A joia promete uma percentagem de TAXA - `+9 %` são nove por cento mais tiros
 * por segundo. Encurtar o intervalo em 9 % daria `base x 0,91`, que é `+9,89 %`
 * de taxa: uma etiqueta a mentir por um ponto percentual inteiro. A recíproca é
 * a única leitura que faz a etiqueta ser verdade.
 *
 * ## Porque milli-tiques, e nunca tiques inteiros
 *
 * Porque um inteiro não sabe exprimir uma percentagem. Com um intervalo-base de
 * 4 tiques, `+9 %` pede 3,67, e truncar isso para 3 é `+33 %` - uma joia a
 * conceder quatro vezes o que anuncia. Os milli-tiques dão ao acumulador onde
 * guardar o resto, que é a mesma resposta que o acumulador inteiro de 16 ms da
 * caça dá à mesma pergunta.
 *
 * ## O truncamento, e o que NÃO se faz com ele
 *
 * A divisão trunca, o que encurta o intervalo e entrega até **+0,024 pontos
 * percentuais** acima da etiqueta no pior caso. Aprovado como está no gate
 * V28G.D: compensar com um tiro extra, um tiro suprimido, uma alternância ou
 * aleatoriedade trocaria um desvio de um quadragésimo de ponto percentual por
 * uma cadência que deixa de ser determinística - e uma autoridade e a sua
 * re-simulação têm de concordar tique a tique.
 *
 * ## Aritmética em `bigint`
 *
 * Porque a percentagem chega como `bigint` do resolvedor de efeitos, que a
 * mantém assim por causa das grandezas sem tecto. Converter antes de somar seria
 * um sítio onde um perfil absurdo perderia dígitos em silêncio. O quociente é
 * sempre menor ou igual à base, portanto o `Number` final é exato.
 *
 * ## A identidade
 *
 * Sem percentagem nenhuma devolve a base **pelo mesmo `number`**, sem tocar em
 * aritmética. Não é uma otimização: é o que garante que uma partida sem a joia
 * tem o MESMO intervalo, e não um intervalo que calhou dar o mesmo.
 */
export function composeFireIntervalMilliTicks(
  baseMilliTicks: number,
  attackSpeedBps: bigint,
): number {
  if (attackSpeedBps === 0n) {
    return baseMilliTicks;
  }
  if (attackSpeedBps < 0n) {
    throw new RangeError(`negative attack speed bps in a combat profile: ${attackSpeedBps}`);
  }
  const base = BigInt(baseMilliTicks);
  const scaled = (base * BPS_ONE) / (BPS_ONE + attackSpeedBps);
  /*
   * Um intervalo de zero seria uma salva por tique sem limite, e nenhuma
   * magnitude de joia lá chega: `bps` teria de ser maior que `base x 10000`.
   * O piso de um milli-tique é uma recusa a produzir isso, não um tecto de item.
   */
  return Math.max(MIN_FIRE_INTERVAL_MILLI_TICKS, Number(scaled));
}

/** Quantos milissegundos tem um segundo. Nomeado para a recíproca não o esconder. */
const MS_PER_SECOND = 1000;

/**
 * **A ponte entre as duas maneiras de dizer cadência** (gate V28I.W).
 *
 * A caça conta **ataques por segundo**; o duelo e o mundo aberto contam
 * **milli-tiques entre salvas**. São a mesma grandeza ao contrário, e a
 * conversão tinha de existir algures no momento em que um limite passou a
 * congelar a cadência REAL de uma conta num perfil de sala.
 *
 * Vive aqui — e não no limite que a chama — porque é a mesma lei que
 * {@link composeFireIntervalMilliTicks} exprime: um intervalo é o inverso de uma
 * taxa, e duas expressões disso em ficheiros diferentes seriam duas respostas a
 * "quantas vezes por segundo dispara esta nave".
 *
 * ## A fórmula
 *
 *     milli_tiques_por_ataque = 1000 x 1000 / (taxa x tickMs)
 *
 * Um segundo tem `1000 / tickMs` tiques, e cada tique tem mil milli-tiques.
 * Com `tickMs = 50` e uma taxa de um ataque por segundo dá 20 000 milli-tiques,
 * que são os vinte tiques de um segundo — e é assim que a leitura se confere.
 *
 * ## O truncamento, e porque é o mesmo desta folha
 *
 * A divisão trunca, o que ENCURTA o intervalo: o casco dispara no máximo um
 * milli-tique mais cedo do que a taxa exata pediria, nunca mais tarde. É a mesma
 * direção e a mesma ordem de grandeza do desvio que a recíproca das percentagens
 * já tem, e aprovada pela mesma razão — compensar com um tiro extra, um tiro
 * suprimido ou uma alternância trocaria um desvio determinístico por uma cadência
 * que uma autoridade e a sua re-simulação deixariam de conseguir reproduzir
 * tique a tique.
 *
 * ## Sem tecto, e com uma recusa
 *
 * Não há máximo: uma taxa enorme dá um intervalo de um milli-tique e o casco
 * dispara muito, que é exatamente o que o item concede. O piso de um é a mesma
 * recusa a produzir uma salva por tique sem limite que a função acima faz, e não
 * um tecto de cadência — `CLAUDE.md` proíbe reduzir o que um item dá para manter
 * os visuais confortáveis, e nada aqui o reduz.
 *
 * Uma taxa não positiva, não finita ou um tique não positivo são um caminho de
 * emissão avariado e não um casco, portanto são recusados em vez de saneados.
 */
/**
 * **A leitura ao contrário: um intervalo, dito em salvas por segundo** (V28I.AE).
 *
 * A inversa exata de {@link fireIntervalMilliTicksForRate}, e vive ao lado dela
 * pela mesma razão que ela vive aqui: são a mesma lei. Uma segunda expressão
 * disto noutro ficheiro seria uma segunda resposta a "quantas vezes por segundo
 * dispara esta nave", e a primeira a divergir seria aquela em que ninguém tocou.
 *
 * ## Em MILÉSIMOS, e nunca uma vírgula flutuante
 *
 *     milli_salvas_por_segundo = 1000 x 1000 x 1000 / (intervalo x tickMs)
 *
 * Porque isto existe para ser MOSTRADO, e um número que atravessa uma rede é um
 * inteiro. Uma cadência de 540,54 salvas por segundo viaja como `540540`, e quem
 * desenha divide por mil. `CLAUDE.md` diz o mesmo sobre percentagens em basis
 * points e sobre grandezas em milli: a fração vive na unidade, nunca no tipo.
 *
 * ## Só para leitura
 *
 * Nada de mecânico sai daqui. O acumulador conta em milli-tiques e continua a
 * contar em milli-tiques; isto é a tradução para uma frase num painel, e o
 * truncamento é o de uma etiqueta e não o de uma cadência.
 */
export function milliVolleysPerSecondForInterval(
  intervalMilliTicks: number,
  tickMs: number,
): number {
  if (!Number.isSafeInteger(intervalMilliTicks) || intervalMilliTicks < 1) {
    throw new RangeError(`an interval is a positive whole milli-tick, got ${intervalMilliTicks}`);
  }
  if (!Number.isFinite(tickMs) || tickMs <= 0) {
    throw new RangeError(`a tick lasts a positive number of ms, got ${tickMs}`);
  }
  return Math.trunc(
    (MS_PER_SECOND * MILLI_TICKS_PER_TICK * MILLI_PER_UNIT) / (intervalMilliTicks * tickMs),
  );
}

/** Mil milésimos são uma unidade. Nomeado para a conversão acima não o esconder. */
const MILLI_PER_UNIT = 1000;

export function fireIntervalMilliTicksForRate(attacksPerSecond: number, tickMs: number): number {
  if (!Number.isFinite(attacksPerSecond) || attacksPerSecond <= 0) {
    throw new RangeError(`an attack rate is finite and positive, got ${attacksPerSecond}`);
  }
  if (!Number.isFinite(tickMs) || tickMs <= 0) {
    throw new RangeError(`a tick lasts a positive number of ms, got ${tickMs}`);
  }
  const milliTicks = (MS_PER_SECOND * MILLI_TICKS_PER_TICK) / (attacksPerSecond * tickMs);
  if (!Number.isFinite(milliTicks)) {
    throw new RangeError(`an attack rate of ${attacksPerSecond} has no expressible interval`);
  }
  return Math.max(MIN_FIRE_INTERVAL_MILLI_TICKS, Math.floor(milliTicks));
}
